"use client"

import { yupResolver } from "@hookform/resolvers/yup"
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import * as yup from "yup"

import { useTimeCountDown } from "../../utils/useTimeCountDown"
import { ModalBase } from "../ModalBase"

const VERIFY_CODE_MODAL_PANEL_STYLES =
  "w-[calc(100vw-32px)] max-w-[480px] pt-12 px-6 pb-10 font-mono sm:px-[54px] flex flex-col items-center"

const VERIFY_CODE_MODAL_CONTAINER_STYLES =
  "!px-4 !pt-4 !pb-[calc(env(safe-area-inset-bottom)+16px)] sm:!p-4"

const VERIFY_CODE_MODAL_VIEWPORT_STYLES = "overflow-y-auto overscroll-contain"

const OTP_INPUT_BASE_STYLES =
  "outline-none !w-[42px] !h-[56px] rounded-lg border font-mono text-primary-h4 text-center"

const getOtpInputStyles = (hasError: boolean) =>
  `${OTP_INPUT_BASE_STYLES} ${
    hasError
      ? "border-error text-error placeholder:text-error placeholder:opacity-30"
      : "border-borderPrimaryLight text-textPrimary placeholder:text-textSecondary focus:border-borderPrimary"
  }`

interface VerifyCodeProps {
  show: boolean
  onClose: () => void
  title: string
  description: string
  codeLength: number
  onResend: (whatsapp?: boolean) => Promise<void> | void
  onConfirm: (code: string) => Promise<void>
  isSubmitting?: boolean
  error?: string
  isPhoneVerification?: boolean
  modalViewportStyles?: string
  modalContainerStyles?: string
  modalPanelStyles?: string
}

interface FormInputs {
  otp: string
}

const VerifyCode: FC<VerifyCodeProps> = ({
  show,
  onClose,
  title,
  description,
  codeLength,
  onResend,
  onConfirm,
  isSubmitting = false,
  error,
  isPhoneVerification = false,
  modalViewportStyles,
  modalContainerStyles,
  modalPanelStyles,
}) => {
  const { timeLeft, resetTimer } = useTimeCountDown()
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([])
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isExternalErrorDismissed, setIsExternalErrorDismissed] = useState(false)

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        otp: yup.string().required().length(codeLength),
      }),
    [codeLength],
  )

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
  })

  const clearSubmitTimeout = useCallback(() => {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current)
      submitTimeoutRef.current = null
    }
  }, [])

  const shouldAutoFocusOnOpen = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false
    }

    return window.matchMedia("(hover: hover) and (pointer: fine)").matches
  }, [])

  const focusOtpInput = useCallback(
    (index: number) => {
      const nextIndex = Math.max(0, Math.min(codeLength - 1, index))
      const input = otpInputRefs.current[nextIndex]
      if (!(input instanceof HTMLInputElement)) {
        return false
      }

      input.focus({ preventScroll: true })
      input.select()
      return document.activeElement === input
    },
    [codeLength],
  )

  const focusFirstOtpInput = useCallback(() => {
    return focusOtpInput(0)
  }, [focusOtpInput])

  useEffect(() => {
    if (!show && !error) {
      return
    }
    setIsExternalErrorDismissed(false)
  }, [error, show])

  // Clean up form values whenever modal is shown
  useEffect(() => {
    if (!show) {
      return
    }

    reset()
    clearErrors()
    resetTimer()

    if (!shouldAutoFocusOnOpen()) {
      return () => {
        clearSubmitTimeout()
      }
    }

    const attemptFocus = (attempt = 0) => {
      if (focusFirstOtpInput() || attempt >= 4) {
        focusTimeoutRef.current = null
        return
      }

      focusTimeoutRef.current = setTimeout(
        () => {
          attemptFocus(attempt + 1)
        },
        attempt === 0 ? 50 : 150,
      )
    }

    attemptFocus()

    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
        focusTimeoutRef.current = null
      }
      clearSubmitTimeout()
    }
  }, [
    show,
    clearErrors,
    reset,
    resetTimer,
    clearSubmitTimeout,
    focusFirstOtpInput,
    shouldAutoFocusOnOpen,
  ])

  useEffect(
    () => () => {
      clearSubmitTimeout()
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
    },
    [clearSubmitTimeout],
  )

  const onSubmit = useCallback(
    async (data: FormInputs) => {
      try {
        await onConfirm(data.otp)
      } catch (_err) {
        setError("root", { message: error || "Please check the code and try again!" })
      }
    },
    [error, onConfirm, setError],
  )

  const handleResendCode = (whatsapp?: boolean) => {
    resetTimer()
    void onResend(whatsapp)
  }

  const handleClose = () => {
    clearSubmitTimeout()
    reset()
    clearErrors()
    onClose()
  }

  const clearOtpErrors = useCallback(() => {
    if (errors.otp) {
      clearErrors("otp")
    }
    if (errors.root) {
      clearErrors("root")
    }
    if (error && !isExternalErrorDismissed) {
      setIsExternalErrorDismissed(true)
    }
  }, [clearErrors, error, errors.otp, errors.root, isExternalErrorDismissed])

  const scheduleAutoSubmit = useCallback(
    (nextOtp: string) => {
      if (nextOtp.length !== codeLength || isSubmitting) {
        return
      }

      clearSubmitTimeout()
      submitTimeoutRef.current = setTimeout(() => {
        void handleSubmit(onSubmit)()
      }, 100)
    },
    [clearSubmitTimeout, codeLength, handleSubmit, isSubmitting, onSubmit],
  )

  return (
    <ModalBase
      addBgOpacity
      dialogContainerStyles={`${VERIFY_CODE_MODAL_CONTAINER_STYLES} ${modalContainerStyles ?? ""}`}
      dialogPanelStyles={`${VERIFY_CODE_MODAL_PANEL_STYLES} ${modalPanelStyles ?? ""}`}
      dialogViewportStyles={`${VERIFY_CODE_MODAL_VIEWPORT_STYLES} ${modalViewportStyles ?? ""}`}
      disableInitialFocus
      disableVisualViewportTracking
      onClose={handleClose}
      show={show}
      showCloseIcon={true}
    >
      <div className="flex flex-col items-center">
        <h2 className="text-primary-h2 text-center mb-4">{title}</h2>
        <p
          className="text-body-lg text-center text-textPrimary mb-6 whitespace-pre-wrap"
          data-amp-mask
        >
          {description}
        </p>

        <form className="w-full max-w-[320px]" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <div className="mb-6 flex self-center justify-center space-x-2">
            <Controller
              control={control}
              name="otp"
              render={({ field: { onChange, value } }) => {
                const externalError = isExternalErrorDismissed ? undefined : error
                const hasError = !!(errors.root || externalError)
                const otpFields = Array.from({ length: codeLength }, (_, index) => ({
                  char: value?.[index] ?? "",
                  id: `otp-${index}`,
                  index,
                }))
                return (
                  <div className="flex gap-2" data-verify-code-otp-container="true">
                    {otpFields.map(({ char: otpChar, id, index }) => (
                      <input
                        aria-label={`Please enter OTP character ${index + 1}`}
                        className={getOtpInputStyles(hasError)}
                        enterKeyHint={index === codeLength - 1 ? "done" : "next"}
                        inputMode="numeric"
                        key={id}
                        maxLength={codeLength}
                        onChange={(event) => {
                          clearOtpErrors()

                          const enteredValue = event.target.value.replace(/\s+/g, "")
                          const nextChars = otpFields.map(({ char }) => char)

                          if (!enteredValue) {
                            nextChars[index] = ""
                            onChange(nextChars.join(""))
                            return
                          }

                          const charsToApply = enteredValue.slice(0, codeLength - index).split("")
                          let lastFilledIndex = index

                          charsToApply.forEach((char, offset) => {
                            const targetIndex = index + offset
                            nextChars[targetIndex] = char
                            lastFilledIndex = targetIndex
                          })

                          const nextOtp = nextChars.join("")
                          onChange(nextOtp)
                          scheduleAutoSubmit(nextOtp)

                          if (lastFilledIndex < codeLength - 1) {
                            focusOtpInput(lastFilledIndex + 1)
                          }
                        }}
                        onFocus={(event) => {
                          event.target.select()
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Backspace") {
                            event.preventDefault()
                            clearOtpErrors()

                            const nextChars = otpFields.map(({ char }) => char)
                            if (nextChars[index]) {
                              nextChars[index] = ""
                              onChange(nextChars.join(""))
                              return
                            }

                            if (index > 0) {
                              nextChars[index - 1] = ""
                              onChange(nextChars.join(""))
                              focusOtpInput(index - 1)
                            }
                            return
                          }

                          if (event.key === "ArrowLeft") {
                            event.preventDefault()
                            focusOtpInput(index - 1)
                            return
                          }

                          if (event.key === "ArrowRight") {
                            event.preventDefault()
                            focusOtpInput(index + 1)
                          }
                        }}
                        onPaste={(event) => {
                          event.preventDefault()
                          clearOtpErrors()

                          const pastedValue = event.clipboardData
                            .getData("text/plain")
                            .replace(/\s+/g, "")
                            .slice(0, codeLength - index)

                          if (!pastedValue) {
                            return
                          }

                          const nextChars = otpFields.map(({ char }) => char)
                          const charsToApply = pastedValue.split("")
                          let lastFilledIndex = index

                          charsToApply.forEach((char, offset) => {
                            const targetIndex = index + offset
                            nextChars[targetIndex] = char
                            lastFilledIndex = targetIndex
                          })

                          const nextOtp = nextChars.join("")
                          onChange(nextOtp)
                          scheduleAutoSubmit(nextOtp)

                          if (lastFilledIndex < codeLength - 1) {
                            focusOtpInput(lastFilledIndex + 1)
                          } else {
                            focusOtpInput(lastFilledIndex)
                          }
                        }}
                        pattern="[0-9]*"
                        placeholder="X"
                        ref={(element) => {
                          otpInputRefs.current[index] = element
                        }}
                        type="tel"
                        value={otpChar}
                      />
                    ))}
                  </div>
                )
              }}
            />
          </div>

          {(errors.root || (error && !isExternalErrorDismissed)) && (
            <p className="mb-6 -mt-3 text-center text-body-sm text-error">
              {errors.root?.message || error}
            </p>
          )}

          <div className="mb-6 text-center">
            <p className="text-body-md leading-4 uppercase">DIDN&apos;T RECEIVE A CODE?</p>
            {timeLeft === 0 ? (
              isPhoneVerification ? (
                <div className="font-mono text-body-md font-medium leading-[20px] uppercase">
                  <span>RESEND </span>
                  <button
                    className="underline"
                    onClick={() => handleResendCode(false)}
                    type="button"
                  >
                    VIA SMS
                  </button>
                  <span> / </span>
                  <button
                    className="underline"
                    onClick={() => handleResendCode(true)}
                    type="button"
                  >
                    VIA WHATSAPP
                  </button>
                </div>
              ) : (
                <button
                  className="font-mono text-body-md font-medium leading-[20px] uppercase underline"
                  onClick={() => handleResendCode()}
                  type="button"
                >
                  Resend code
                </button>
              )
            ) : (
              <p className="text-body-md leading-4 uppercase">TRY AGAIN IN {timeLeft}...</p>
            )}
          </div>
        </form>

        <p className="text-center text-body-md text-textPrimary font-mono">
          Need help?{" "}
          <a
            className="underline"
            href="mailto:ok@werz.at?subject=Verify%20phone%20number%20issue"
            rel="noreferrer"
            target="_blank"
          >
            Contact us
          </a>
        </p>
      </div>
    </ModalBase>
  )
}

export default VerifyCode
